import { Component } from '@theme/component';

/**
 * Product description that starts collapsed (lead paragraph + fading preview)
 * and expands with "Leer descripción completa". Short descriptions that fit in
 * the preview are shown whole and the button is hidden.
 *
 * @typedef {object} EloraDescripcionRefs
 * @property {HTMLElement} body - Collapsible part of the description
 * @property {HTMLButtonElement} toggle - Expands or collapses the body
 *
 * @extends Component<EloraDescripcionRefs>
 */
class EloraDescripcion extends Component {
  requiredRefs = ['body', 'toggle'];

  connectedCallback() {
    super.connectedCallback();
    this.#hideToggleIfShort();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#hideToggleIfShort();
  }

  /** Shows short descriptions whole instead of fading a few lines. */
  #hideToggleIfShort() {
    if (!this.hasAttribute('data-collapsed')) return;

    const { body, toggle } = this.refs;
    const collapsed = body.clientHeight;
    // Leave some room so the button never reveals just a line or two
    if (body.scrollHeight <= collapsed + 48) {
      this.removeAttribute('data-collapsed');
      toggle.hidden = true;
    }
  }

  /** @param {MouseEvent} [event] */
  toggleDescription(event) {
    event?.preventDefault();
    const { toggle } = this.refs;
    const expand = this.hasAttribute('data-collapsed');

    this.toggleAttribute('data-collapsed', !expand);
    toggle.setAttribute('aria-expanded', String(expand));

    // After collapsing, bring the shortened description back into view
    if (!expand && this.getBoundingClientRect().top < 0) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
    }
  }
}

if (!customElements.get('elora-descripcion')) {
  customElements.define('elora-descripcion', EloraDescripcion);
}
